import { GrowthBookService } from 'src/app/services/growthbook/service';
import { AuthCompany } from 'src/models';

export const UPSELL_SETUP_FLOW_FLAG = 'upsell-setup-flow';

export function isUpsellSetupFlowEnabled(growthbook: GrowthBookService): boolean {
  return growthbook.getFeatureValue<boolean>(UPSELL_SETUP_FLOW_FLAG, false);
}

export function isBasicPricingPlan(company?: Pick<AuthCompany, 'pricingPlan'> | null): boolean {
  return !!company?.pricingPlan?.includes('basic');
}

/** Basic: schedules:upsell OR upsell-setup-flow. Other plans: schedules:upsell only. */
export function isSchedulesUpsellEnabled(
  growthbook: GrowthBookService,
  company?: Pick<AuthCompany, 'pricingPlan'> | null,
): boolean {
  const perFeatureUpsell = !!growthbook.getFeatureValue<boolean>('schedules:upsell', false);
  if (!isBasicPricingPlan(company)) {
    return perFeatureUpsell;
  }
  return perFeatureUpsell || isUpsellSetupFlowEnabled(growthbook);
}

/** True when user should be sent to /upgrade/schedules instead of the report. */
export function shouldRedirectToSchedulesUpsell(
  growthbook: GrowthBookService,
  company?: Pick<AuthCompany, 'pricingPlan' | 'companySettings'> | null,
): boolean {
  // Non-basic plans include schedules on-plan. The report/company-settings own
  // the enable/disable toggle, so a toggle that is currently off must NOT bounce
  // the user to the upsell — otherwise Standard/Premium users with schedules
  // disabled-by-default get stuck on /upgrade/schedules (unauthorized loop).
  if (!isBasicPricingPlan(company)) {
    return false;
  }
  if (company?.companySettings?.workScheduleFeature) {
    return false;
  }
  return isSchedulesUpsellEnabled(growthbook, company);
}

/** True when user should be sent to /upgrade/web-app-usage instead of the report. */
export function shouldRedirectToWebAppUsageUpsell(
  growthbook: GrowthBookService,
  company?: Pick<AuthCompany, 'pricingPlan' | 'companySettings'> | null,
): boolean {
  // Paid plans include Web & App Usage on-plan. Once tracking is enabled (or
  // still intentionally off on Standard+), show the report — not the Basic upsell.
  if (!isBasicPricingPlan(company)) {
    return false;
  }
  if (company?.companySettings?.webAndAppTracking
    && company.companySettings.webAndAppTracking !== 'off') {
    return false;
  }
  return !!growthbook.getFeatureValue<boolean>('web-and-app-usage:upsell', false);
}
